import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Eye, FileText, Server, Users } from 'lucide-react';

export const PrivacyPolicyPage = () => {
  const navigate = useNavigate();
  const lastUpdated = "December 2025";

  return (
    <div className="min-h-screen bg-medex-bg">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center text-slate-400 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center space-x-4 mb-8">
            <div className="w-12 h-12 rounded-xl bg-neon-cyan/20 flex items-center justify-center">
              <Shield className="w-6 h-6 text-neon-cyan" />
            </div>
            <div>
              <h1 className="text-3xl font-heading font-bold text-white">Privacy Policy</h1>
              <p className="text-slate-400">Last updated: {lastUpdated}</p>
            </div>
          </div>

          <div className="glass-card p-8 space-y-8">
            <section>
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
                <Eye className="w-5 h-5 mr-2 text-neon-cyan" />
                Information We Collect
              </h2>
              <div className="text-slate-300 space-y-4">
                <p>MedexOVA ("we," "our," or "us") collects information to provide and improve our healthcare transportation technology services. We collect:</p>
                <ul className="list-disc pl-6 space-y-2 text-slate-400">
                  <li><strong className="text-white">Contact Information:</strong> Name, email address, phone number, company name</li>
                  <li><strong className="text-white">Business Information:</strong> Fleet size, service areas, trip volumes, dispatch software used</li>
                  <li><strong className="text-white">Usage Data:</strong> How you interact with our platform, pages visited, features used</li>
                  <li><strong className="text-white">Device Information:</strong> IP address, browser type, operating system</li>
                  <li><strong className="text-white">Trip Data:</strong> For users of our dispatch services, trip origins, destinations, and timing (never patient medical information)</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
                <FileText className="w-5 h-5 mr-2 text-neon-cyan" />
                How We Use Your Information
              </h2>
              <div className="text-slate-300 space-y-4">
                <ul className="list-disc pl-6 space-y-2 text-slate-400">
                  <li>To provide and maintain our services</li>
                  <li>To process your requests and applications</li>
                  <li>To communicate with you about our services, updates, and promotional offers</li>
                  <li>To improve our platform and develop new features</li>
                  <li>To calculate insurance premiums (for activity-based insurance users)</li>
                  <li>To comply with legal obligations</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
                <Lock className="w-5 h-5 mr-2 text-neon-cyan" />
                Data Security
              </h2>
              <div className="text-slate-300 space-y-4">
                <p>We implement industry-standard security measures to protect your information:</p>
                <ul className="list-disc pl-6 space-y-2 text-slate-400">
                  <li>SSL/TLS encryption for all data transmission</li>
                  <li>Encrypted data storage</li>
                  <li>Regular security audits and vulnerability assessments</li>
                  <li>Access controls and authentication requirements</li>
                  <li>Employee training on data protection</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
                <Server className="w-5 h-5 mr-2 text-neon-cyan" />
                Data Retention & Sharing
              </h2>
              <div className="text-slate-300 space-y-4">
                <p>We retain your information for as long as necessary to provide our services and comply with legal obligations. We may share your information with:</p>
                <ul className="list-disc pl-6 space-y-2 text-slate-400">
                  <li><strong className="text-white">Service Providers:</strong> Third parties who assist in operating our platform</li>
                  <li><strong className="text-white">Insurance Partners:</strong> For processing insurance applications and claims</li>
                  <li><strong className="text-white">Legal Requirements:</strong> When required by law or to protect our rights</li>
                </ul>
                <p className="text-slate-400">We never sell your personal information to third parties.</p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
                <Users className="w-5 h-5 mr-2 text-neon-cyan" />
                Your Rights
              </h2>
              <div className="text-slate-300 space-y-4">
                <p>You have the right to:</p>
                <ul className="list-disc pl-6 space-y-2 text-slate-400">
                  <li>Access your personal information</li>
                  <li>Correct inaccurate information</li>
                  <li>Request deletion of your information</li>
                  <li>Opt-out of marketing communications</li>
                  <li>Data portability (receive your data in a standard format)</li>
                </ul>
                <p>To exercise these rights, contact us at <a href="mailto:privacy@medexova.com" className="text-neon-cyan hover:underline">privacy@medexova.com</a></p>
              </div>
            </section>

            <section id="sms-consent">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
                <svg className="w-5 h-5 mr-2 text-neon-cyan" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                SMS/Text Message Policy
              </h2>
              <div className="text-slate-300 space-y-4">
                <div className="p-4 bg-neon-green/10 border border-neon-green/30 rounded-lg">
                  <h3 className="text-white font-semibold mb-2">Consent & Opt-In</h3>
                  <p className="text-slate-400 mb-3">
                    Users opt in to receive SMS messages during account registration or when booking a ride through our app. 
                    At the point of opt-in, users are required to check a consent box agreeing to receive SMS notifications.
                  </p>
                  <p className="text-slate-400">
                    The checkbox is <strong className="text-white">not pre-checked</strong> and consent is required before proceeding. 
                    <strong className="text-white"> No messages are sent without user consent.</strong>
                  </p>
                </div>

                <div className="p-4 bg-neon-cyan/10 border border-neon-cyan/30 rounded-lg">
                  <h3 className="text-white font-semibold mb-2">SMS Disclosure Statement</h3>
                  <p className="text-white text-sm leading-relaxed">
                    Message frequency may vary. Standard message and data rates may apply. Reply <strong className="text-neon-cyan">STOP</strong> to opt out. Reply <strong className="text-neon-cyan">HELP</strong> for assistance. <strong>Your mobile information will not be sold or shared with third parties for promotional or marketing purposes.</strong>
                  </p>
                </div>

                <h3 className="text-white font-semibold">Types of SMS Messages</h3>
                <p>By opting in, you may receive the following types of SMS notifications:</p>
                <ul className="list-disc pl-6 space-y-2 text-slate-400">
                  <li><strong className="text-white">Ride Notifications:</strong> Trip confirmations, driver arrival alerts, ride status updates</li>
                  <li><strong className="text-white">Account Alerts:</strong> Login verification codes, password resets, security notifications</li>
                  <li><strong className="text-white">Credential Reminders:</strong> Document expiration alerts, verification status updates</li>
                  <li><strong className="text-white">Service Updates:</strong> Important platform announcements, scheduled maintenance</li>
                </ul>

                <h3 className="text-white font-semibold">Message Frequency & Costs</h3>
                <p className="text-slate-400">
                  Message frequency varies based on your activity and notification preferences. Standard message and data rates may apply 
                  depending on your mobile carrier plan. MedexOVA does not charge for SMS messages, but your carrier may.
                </p>

                <h3 className="text-white font-semibold">Data Privacy</h3>
                <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                  <p className="text-white font-medium mb-2">Your mobile information will NOT be:</p>
                  <ul className="list-disc pl-6 space-y-1 text-slate-400">
                    <li>Sold to third parties</li>
                    <li>Shared with third parties for promotional purposes</li>
                    <li>Shared with third parties for marketing purposes</li>
                    <li>Used for any purpose other than providing our services</li>
                  </ul>
                </div>

                <h3 className="text-white font-semibold">How to Opt-Out</h3>
                <div className="p-4 bg-white/5 rounded-lg">
                  <p className="text-slate-400 mb-2">You can opt-out of SMS messages at any time by:</p>
                  <ul className="list-disc pl-6 space-y-1 text-slate-400">
                    <li>Replying <strong className="text-neon-cyan">STOP</strong> to any SMS message from MedexOVA</li>
                    <li>Updating your notification preferences in your account settings</li>
                    <li>Contacting us at <a href="mailto:support@medexova.com" className="text-neon-cyan hover:underline">support@medexova.com</a></li>
                  </ul>
                  <p className="text-slate-400 mt-2">
                    Reply <strong className="text-neon-cyan">HELP</strong> to any message for assistance.
                  </p>
                </div>

                <h3 className="text-white font-semibold">Carrier Disclaimer</h3>
                <p className="text-slate-400">
                  Carriers are not liable for delayed or undelivered messages. Message delivery is subject to effective transmission 
                  from your network operator and is not guaranteed.
                </p>

                <h3 className="text-white font-semibold">Supported Carriers</h3>
                <p className="text-slate-400">
                  Our SMS service is compatible with all major US carriers including AT&T, Verizon, T-Mobile, Sprint, and most regional carriers. 
                  International messaging may not be available in all regions.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">HIPAA Compliance</h2>
              <div className="p-4 bg-neon-cyan/10 border border-neon-cyan/30 rounded-lg">
                <p className="text-slate-300">
                  MedexOVA is committed to HIPAA compliance for all healthcare-related data. While we process trip logistics data, 
                  we do not store or process Protected Health Information (PHI). Our platform is designed to integrate with 
                  HIPAA-compliant healthcare systems while maintaining appropriate data boundaries.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">Contact Us</h2>
              <div className="text-slate-300">
                <p>For privacy-related inquiries:</p>
                <p className="mt-2 text-slate-400">
                  Email: <a href="mailto:privacy@medexova.com" className="text-neon-cyan hover:underline">privacy@medexova.com</a><br />
                  Address: MedexOVA, LLC<br />
                </p>
              </div>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
